// T-0157 — the ◐/● law: two tenses, two registers.
//
// This is ✓-requires-receipt generalized to time. A badge is not a decoration; it
// names WHO owns the clock, and that decides which tense the product is allowed
// to speak:
//
//   ● platform schedule — a row in `scheduled_requests`, swept by the server.
//     Trustworthy by construction: Tizok owns the timer, so "next 8:00" is a
//     promise it can keep. ● is the ONLY badge that may speak a bare future tense.
//
//   ◐ agent-armed cron — a timer inside an agent's own loop. Forever a claim.
//     The badge is derived ONLY from attested beats (`beat.rs` §5.7 counters);
//     a self-reported schedule is *unrepresentable* here. The most it can ever
//     earn is an attributed claim: "◐ says it'll wake at 8:00 · last beat 8:02 ✓".
//
//   amber — the past tense. Tizok guarantees the FIRE, not the turn (execution is
//     device-local), so a Schedule that ran late, or fired into a room where no
//     machine ever took the turn, CONFESSES in plain past tense. Never a modal.
//
// Everything below is pure. The sentences are canon (rnd/SCHEDULED_REQUESTS_DECISION.md
// §"Aliveness"), pinned character-for-character by `scheduleAliveness.test.ts`,
// and none of them may ever contain the words cron, job, or ticket.

import { formatLocalTime, isoToLocalTime, repeatRuleToLocalTime } from './schedule';
import type { FireSummary, ScheduleFire } from './api';

/**
 * The sweep's own resolution: it ticks every 60s, so "an 8:00 Request fires by
 * 8:01 at the latest, which is inside the noise floor of 'the org woke up and
 * started thinking'" (src/application/scheduled_requests.rs). A fire inside that
 * window ran on time *by construction* — confessing it would be theatre. Past it,
 * the server really was late, and says so.
 */
export const ON_TIME_GRACE_SECONDS = 60;

/** The outcomes in which the fire's Request actually landed in the space. */
const LANDED_OUTCOMES = ['posted', 'deduped'];

/**
 * What a ● platform Schedule is doing right now.
 *
 * `missed` carries `firedAt: null` when the Request never landed at all (the
 * space was halted or archived at the scheduled instant) — there is no run to
 * report, so the sentence stops after the confession and offers no receipt.
 */
export type ScheduleState =
  | { kind: 'paused' }
  | { kind: 'asleep'; wakesAt: string }
  | { kind: 'armed'; nextFireAt: string }
  | { kind: 'missed'; scheduledFor: string; firedAt: string | null }
  | { kind: 'awaitingMachine'; scheduledFor: string };

/** The amber states — the two the product confesses in the past tense. */
export function isAmber(state: ScheduleState): boolean {
  return state.kind === 'missed' || state.kind === 'awaitingMachine';
}

/** A fire the user would read as late: the clock they see disagrees, and it
 *  disagrees by more than the sweep's own resolution.
 *
 *  Exported for the canvas heartbeat (T-0177), which must not call a fire late for the
 *  seconds the sweep itself takes — the ONE definition of "late" lives here. */
export function fireRanLate(fire: FireSummary): boolean {
  return fire.late_by_seconds > ON_TIME_GRACE_SECONDS;
}

const ranLate = fireRanLate;

/** Did this fire's Request reach the space at all? */
function landed(fire: FireSummary): boolean {
  return LANDED_OUTCOMES.includes(fire.outcome);
}

/**
 * What ONE fire has to confess, or `null` if it has nothing to answer for.
 *
 * The single source of the amber states, so the sentence a fire speaks in the
 * row is the same one its tick speaks in the history strip. Precedence:
 *
 *  1. **missed, no receipt** — the Request never reached the space (halted or
 *     archived at the scheduled instant). There is no run to receipt.
 *  2. **awaitingMachine** — it landed, and nothing answered. This outranks a
 *     late run because the ✓ ending the late confession is a receipt for a turn
 *     that, here, never happened.
 *  3. **missed, with receipt** — it landed, late, and a machine did take it.
 */
function fireConfession(fire: FireSummary): ScheduleState | null {
  if (!landed(fire)) {
    return { kind: 'missed', scheduledFor: fire.scheduled_for, firedAt: null };
  }
  if (!fire.turn_taken) {
    return { kind: 'awaitingMachine', scheduledFor: fire.scheduled_for };
  }
  if (ranLate(fire)) {
    return { kind: 'missed', scheduledFor: fire.scheduled_for, firedAt: fire.fired_at };
  }
  return null;
}

/** A fire whose tick stays amber in the history strip, forever. */
export function fireIsAmber(fire: FireSummary): boolean {
  return fireConfession(fire) !== null;
}

/**
 * The state machine. Precedence, most-honest-first:
 *
 *  1. **paused** — a silenced Schedule has no next run to speak of. Its stored
 *     `next_fire_at` is the stale one from before it was silenced.
 *  2. whatever the latest fire has to **confess** (see [`fireConfession`]).
 *  3. **asleep** — nothing is wrong; the org is resting between fires and says
 *     when it wakes. A named next-wake, never a fake pulse.
 *  4. **armed** — the ordinary future tense, which only ● may speak.
 *
 * Only the LATEST fire is passed in. "Waiting for a machine" is a claim about
 * now, and an older fire's silence was long ago superseded by the next one.
 */
export function scheduleState(input: {
  enabled: boolean;
  nextFireAt: string;
  /** The space is `sleeping` — known only where the wire exposes that state. */
  spaceAsleep?: boolean;
  latestFire?: FireSummary | null;
}): ScheduleState {
  const { enabled, nextFireAt, spaceAsleep, latestFire } = input;

  if (!enabled) return { kind: 'paused' };

  const confession = latestFire ? fireConfession(latestFire) : null;
  if (confession) return confession;

  if (spaceAsleep) return { kind: 'asleep', wakesAt: nextFireAt };
  return { kind: 'armed', nextFireAt };
}

/** An ISO instant in the reader's own zone: "8:00". `null` when unreadable. */
function at(iso: string): string | null {
  const local = isoToLocalTime(iso);
  return local ? formatLocalTime(local) : null;
}

/**
 * The sentence a ● Schedule speaks about itself, minus its name.
 *
 * An unreadable timestamp degrades to the cadence ("repeats daily") rather than
 * to a time we would be inventing — the same rule `scheduleNextPhrase` follows.
 */
export function scheduleStatePhrase(
  state: ScheduleState,
  opts?: {
    /** A relative age like "2h ago", woven into the awaitingMachine confession so
     *  it reads as a fresh fact, not a pinned-forever banner (T-0200). Absent ⇒
     *  the original tenseless confession. */
    firedAgo?: string;
  },
): string {
  switch (state.kind) {
    case 'paused':
      return 'paused';
    case 'asleep': {
      // Scoped to THIS Schedule, which is the row the sentence sits in: the org is
      // asleep, and this Schedule wakes it at 8:00. A shorter check-in timeout may
      // wake it sooner — that cadence is named beside this one on the meta-line,
      // and is not a promise this row is making.
      const wakes = at(state.wakesAt);
      return wakes ? `asleep · wakes ${wakes}` : 'asleep';
    }
    case 'armed': {
      const next = at(state.nextFireAt);
      return next ? `next ${next}` : 'repeats daily';
    }
    case 'missed': {
      const due = at(state.scheduledFor);
      if (!due) return 'Missed a run';
      const ran = state.firedAt ? at(state.firedAt) : null;
      // The ✓ is the receipt that it eventually ran. Without a run there is
      // nothing to receipt, and the sentence stops at the confession.
      return ran ? `Didn't run at ${due} — ran ${ran} ✓` : `Didn't run at ${due}`;
    }
    case 'awaitingMachine': {
      const due = at(state.scheduledFor);
      // Canon copy, lowercase "request" and all: the Request left, but a Request
      // nobody picked up is not yet the org's work. The optional age keeps it from
      // reading as pinned-forever — it clears the instant a machine takes the turn
      // (`fireConfession` stops returning this state once `turn_taken`).
      const aged = opts?.firedAgo ? ` ${opts.firedAgo}` : '';
      return due
        ? `${due} request fired${aged} — waiting for a machine`
        : `Request fired${aged} — waiting for a machine`;
    }
  }
}

/**
 * The header REST CHIP — "asleep · wakes 16:40" — for a ● Schedule between fires.
 *
 * An armed ● timer *is* the room resting until its next fire, so the chip speaks
 * the same rest register the space sheet uses (the member wire hides `sleeping`,
 * but the ● Schedule the header already reads names the wake honestly). Amber and
 * paused states are not the chip's job — the confession line carries those — so
 * they return `null`, and a space with no ● Schedule has no rest chip at all
 * (retiring the old permanent "ACTIVE" fake pulse rather than replacing it with a
 * different one).
 */
export function restChipPhrase(state: ScheduleState | null): string | null {
  if (!state) return null;
  if (state.kind === 'asleep') return scheduleStatePhrase(state);
  if (state.kind === 'armed') {
    return scheduleStatePhrase({ kind: 'asleep', wakesAt: state.nextFireAt });
  }
  return null;
}

/**
 * The thread-header affix: "repeats daily · next 8:00".
 *
 * The cadence leads, because a header answers "what is this room's rhythm?".
 * `asleep` already carries its own subject ("asleep · wakes 8:00") and would
 * read as a lie prefixed by a rhythm it is not currently keeping.
 */
export function threadAffixSentence(state: ScheduleState): string {
  if (state.kind === 'asleep') return scheduleStatePhrase(state);
  return `repeats daily · ${scheduleStatePhrase(state)}`;
}

/**
 * The space-sheet meta-line affix: "↻ Daily 8:00", beside "Check-in every 4h".
 *
 * A **cadence**, built from the rule — not a tense, and deliberately not built
 * from `next_fire_at`. "Check-in every 4h" makes no claim about the next check-in
 * and neither does this: it says what the Schedule *is*. That is why the meta-line
 * needs no fire history to stay honest, and why it can sit one line above a row
 * confessing "Didn't run at 8:00 — ran 8:13 ✓" without contradicting it. The next
 * run is the row's business, where a missed fire can correct it.
 */
export function scheduleMetaAffix(repeatRule: string): string | null {
  const local = repeatRuleToLocalTime(repeatRule);
  return local ? `↻ Daily ${formatLocalTime(local)}` : null;
}

/**
 * What ONE past fire says about itself — the label on its tick in the history
 * strip. **History keeps its amber tick**: a fire that ran late, never landed, or
 * was never answered says so forever, in the same sentence it said it in on the
 * day. Only an unremarkable fire gets the short receipt.
 */
export function fireTickLabel(fire: ScheduleFire): string {
  if (fireIsAmber(fire)) {
    // Re-uses the canon confessions verbatim. `nextFireAt` is unread on every
    // branch these three states can reach.
    return scheduleStatePhrase(
      scheduleState({ enabled: true, nextFireAt: fire.fired_at, latestFire: fire }),
    );
  }
  const ran = at(fire.fired_at);
  return ran ? `Ran ${ran} ✓` : 'Ran ✓';
}

// ─────────────────────────────────────────────────────────────────────────────
// ◐ — the only honest surface an agent cron can ever earn
//
// ## Nothing calls this yet, and that is the finding, not an oversight.
//
// The attested beat currently dead-ends before the browser. `beat.rs` publishes
// its counters into a heartbeat *room* (via `/admin/send`, read back by the
// off-box watcher through `/admin/messages`) and into an on-box `atlas-beat.json`
// the desktop shell reads. No `/api` route carries `reconcile_tick` /
// `wake_frame_tick`, and no member or agent shape on the space wire has a beat
// field. So `agentCronState` returns `null` for every input the app can build
// today, and every ◐ surface renders nothing.
//
// That is the specified behaviour, not a stub: *if no attested beat data →
// nothing*. The law ships tested and provenance-gated so that the day a beat read
// path lands, the badge lights up already honest — and until then the product
// stays silent rather than reassuring. Wiring it to `last_activity_at` (the one
// presence-ish field the browser DOES get) would be exactly the self-attested
// timestamp `beat.rs` §5.7 forbids.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * One published beat. `beat.rs` §5.7: **staleness is judged by the commit-gated
 * COUNTERS, never the timestamp** — "a half-wedged daemon could keep writing a
 * fresh 'alive' timestamp while the loop is stuck; the commit-gated counters
 * cannot be faked that way."
 */
export interface AttestedBeat {
  /** Advisory only. Never the proof — only what we print once proof exists. */
  publishedAt: string;
  /** Bumped only after a reconcile pass COMMITS. */
  reconcileTick: number;
  /** Bumped once per delivered wake frame. */
  wakeFrameTick: number;
}

/** What an agent SAYS about its own cron. Never, on its own, renderable. */
export interface AgentCron {
  /** The agent's self-reported next wake. A hope, not a promise. */
  claimedWakeAt?: string | null;
  /** The beat window, oldest → newest. Two beats are the minimum proof of life:
   *  one beat is a timestamp, and a timestamp is exactly what may not be trusted. */
  beats?: readonly AttestedBeat[];
}

export type AgentCronState =
  | { kind: 'agentClaim'; claimedWakeAt: string; lastBeatAt: string }
  | { kind: 'agentBeat'; lastBeatAt: string };

/**
 * Did a commit-gated counter advance between these two beats?
 *
 * Compared across the whole window, never just its last adjacent pair: beats are
 * published on a timer (~15s) while `reconcile_tick` only bumps when a pass
 * *commits*. Two beats in a row routinely carry identical counters for a perfectly
 * healthy daemon, so adjacent comparison would call a live agent dead.
 */
export function beatProgressed(oldest: AttestedBeat, latest: AttestedBeat): boolean {
  return (
    latest.reconcileTick > oldest.reconcileTick || latest.wakeFrameTick > oldest.wakeFrameTick
  );
}

/**
 * The ◐ state, or `null` — and `null` means **render nothing at all**.
 *
 * There is no fallback. An agent cron with no attested beat behind it is
 * unrepresentable: not a grey badge, not a dimmed promise, not "◐ unknown".
 * Nothing. That absence is the moat — the surface is silent rather than
 * reassuring, exactly as `beat.rs` §5.7 requires ("never trust self-attested
 * aliveness").
 *
 * A self-reported wake time may ride along only ONCE the beats have proven the
 * agent is alive to keep it, and even then it is quoted, never asserted.
 */
export function agentCronState(cron: AgentCron | null | undefined): AgentCronState | null {
  const beats = cron?.beats ?? [];
  if (beats.length < 2) return null;

  const latest = beats[beats.length - 1];
  if (!beatProgressed(beats[0], latest)) return null;
  if (!at(latest.publishedAt)) return null;

  const claimedWakeAt = cron?.claimedWakeAt;
  if (claimedWakeAt && at(claimedWakeAt)) {
    return { kind: 'agentClaim', claimedWakeAt, lastBeatAt: latest.publishedAt };
  }
  return { kind: 'agentBeat', lastBeatAt: latest.publishedAt };
}

/**
 * "◐ says it'll wake at 8:00 · last beat 8:02 ✓"
 *
 * *says it'll* is load-bearing. It attributes the future tense to the agent
 * rather than asserting it as the product's own — the grammatical difference
 * between ● and ◐. The ✓ receipts the BEAT, which we verified; never the wake,
 * which we did not.
 */
export function agentCronSentence(state: AgentCronState): string {
  const lastBeat = at(state.lastBeatAt);
  if (state.kind === 'agentBeat') return `◐ last beat ${lastBeat} ✓`;
  return `◐ says it'll wake at ${at(state.claimedWakeAt)} · last beat ${lastBeat} ✓`;
}
